import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Marco Alvarez",
    role: "Owner · Riverside Bistro",
    quote:
      "Four fake reviews were killing us. Go Higher had them removed in 3 weeks. Our rating jumped from 3.6 to 4.7 — and reservations doubled the following month.",
    rating: 5,
    initials: "MA",
    accent: "emerald",
    result: "Rating 3.6 → 4.7",
  },
  {
    name: "Dr. Priya Shah",
    role: "Founder · Bright Smile Dental",
    quote:
      "A disgruntled ex-employee posted 5 false reviews. Every single one was removed. What impressed me most: they refused to touch anything that wasn't clearly a policy violation.",
    rating: 5,
    initials: "PS",
    accent: "gold",
    result: "5 reviews removed",
  },
  {
    name: "Jamal Whitaker",
    role: "Owner · Whitaker HVAC & Air",
    quote:
      "I only paid after each removal was confirmed. Six removed reviews later, our star rating went from 3.9 to 4.8. The pay-after model is the only reason I trusted them.",
    rating: 5,
    initials: "JW",
    accent: "emerald",
    result: "Rating 3.9 → 4.8",
  },
  {
    name: "Laura Chen, Esq.",
    role: "Partner · Chen & Ross Law",
    quote:
      "Ethics matter in our profession. Go Higher was the only firm that said 'no' to grey-area tactics. Two removals, professional documentation, done.",
    rating: 5,
    initials: "LC",
    accent: "gold",
    result: "2 removed in 12 days",
  },
  {
    name: "Simone Marchetti",
    role: "Owner · Sable Hair Studio",
    quote:
      "Their monitoring service caught two new negative reviews within hours. Both were spam, both are gone. I sleep better at night.",
    rating: 5,
    initials: "SM",
    accent: "emerald",
    result: "Monitoring plan",
  },
  {
    name: "Derek Nolan",
    role: "GM · Nolan Auto Body",
    quote:
      "Simple, fair, and effective. Rating climbed 0.9 stars over 60 days. New customers actually mention our Google reviews now — in a good way.",
    rating: 5,
    initials: "DN",
    accent: "gold",
    result: "+0.9★ in 60 days",
  },
];

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < n ? "fill-[#D4AF37] text-[#D4AF37]" : "fill-white/10 text-white/20"
          }`}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  return (
    <section className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
              What clients say
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
              Local businesses. <span className="text-white/50">Measurable wins.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 text-sm text-white/55">
            <div className="flex -space-x-2">
              {["#10B981", "#D4AF37", "#94A3B8"].map((c, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-[#0A0F1C]"
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div>
              <span className="text-white font-semibold">640+</span> owners protected
            </div>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => {
            const isEmerald = t.accent === "emerald";
            return (
              <motion.blockquote
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-white/20 transition-colors"
              >
                <Quote
                  className={`absolute top-4 right-4 w-6 h-6 ${
                    isEmerald ? "text-[#10B981]/25" : "text-[#D4AF37]/25"
                  }`}
                />
                <Stars n={t.rating} />
                <p className="mt-4 text-white/80 text-sm leading-relaxed">"{t.quote}"</p>

                <div
                  className={`mt-4 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                    isEmerald
                      ? "border-[#10B981]/30 text-[#10B981] bg-[#10B981]/5"
                      : "border-[#D4AF37]/30 text-[#D4AF37] bg-[#D4AF37]/5"
                  }`}
                >
                  {t.result}
                </div>

                <footer className="mt-6 flex items-center gap-3">
                  <div
                    className={`w-10 h-10 grid place-items-center rounded-full font-display font-bold text-sm ${
                      isEmerald
                        ? "bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40"
                        : "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40"
                    }`}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{t.name}</div>
                    <div className="text-white/45 text-xs">{t.role}</div>
                  </div>
                </footer>
              </motion.blockquote>
            );
          })}
        </div>
      </div>
    </section>
  );
}
